import type { Locale } from '@et/schema';
import { appCheckHeader } from './app-check.js';
import { authClient } from './auth.js';
import type { ContentRev } from './api.js';

/**
 * O que a app guarda por utilizador.
 *
 * Separado de `api.ts` de propósito: aquilo é conteúdo imutável, cacheável
 * para sempre por versão; isto muda a cada acção e nunca é cacheado por
 * versão de conteúdo. Misturar os dois numa camada só é como se acabam a
 * servir bookmarks de uma versão antiga.
 */

const env = (import.meta as { env?: Record<string, string | undefined> }).env ?? {};
const base = env['VITE_API_ORIGIN'] ?? '';

async function authed<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await authClient().token();
  if (token === null) throw new Error('sem sessão');
  const res = await fetch(`${base}${path}`, {
    ...init,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(await appCheckHeader()),
      ...(init.headers ?? {}),
    },
  });
  if (!res.ok) throw new Error(`${res.status} ${path}`);
  /*
    Nem toda a resposta sem corpo e um 204.
    Isto so tratava o 204, e a rota de denuncia responde 202 — "ficou
    registado, e a decisao e de outra pessoa". O `res.json()` rebentava num
    corpo vazio, a mutacao rejeitava e o `onSuccess` nunca corria: o reporte
    ficava gravado e a interface nao dava sinal nenhum. Um erro que so se ve
    de fora, porque do lado do servidor estava tudo certo.
  */
  if (res.status === 204 || res.status === 202) return undefined as T;
  if (res.headers.get('content-length') === '0') return undefined as T;
  return res.json() as Promise<T>;
}

export interface CourseState {
  readonly courseId: string;
  readonly title: string;
  readonly status: 'not_started' | 'in_progress' | 'completed' | 'updated';
  readonly completionPercentage: number;
  readonly completedLessons: number;
  readonly lessonCount: number;
  readonly currentLessonId: string | null;
  readonly enrolledVersion: number;
  readonly currentVersion: number;
}

export interface LearningCounters {
  readonly totalLessonsCompleted: number;
  readonly totalSourcesOpened: number;
  readonly checkpointsAnswered: number;
  readonly streak: number;
  readonly lastActiveAt: string | null;
}

export interface Dashboard {
  readonly continue: CourseState | null;
  readonly courses: readonly CourseState[];
  readonly recent: readonly { id: string; ref: { type: string; id: string }; action: string; at: string }[];
  readonly counters: LearningCounters;
  readonly checkpoints: { answered: number; correct: number; reflections: number };
}

export interface Profile {
  readonly displayName?: string;
  readonly locale?: string;
  readonly isAnonymous: boolean;
  readonly incognito: boolean;
  readonly preferences: Record<string, unknown>;
}

export interface LessonProgress {
  readonly lessonId: string;
  readonly courseId: string;
  readonly completedAt: string;
  readonly sourcesOpened: readonly string[];
}

export interface Bookmark {
  readonly id: string;
  readonly ref: { readonly type: string; readonly id: string };
  readonly note?: string;
  readonly createdAt: string;
}

export const fetchDashboard = () => authed<Dashboard>('/api/me/dashboard');
export const fetchEnrollments = () => authed<{ items: CourseState[] }>('/api/me/enrollments');
export const fetchLessonProgress = () => authed<{ items: LessonProgress[] }>('/api/me/lesson-progress');
export const fetchProfile = () => authed<Profile>('/api/me/profile');
export const fetchBookmarks = () => authed<{ items: Bookmark[] }>('/api/me/bookmarks');
export const fetchCheckpointResults = () =>
  authed<{ items: { itemId: string; correct: boolean | null }[] }>('/api/me/checkpoint-results');

export const enroll = (courseId: string) =>
  authed<CourseState>(`/api/me/enrollments/${encodeURIComponent(courseId)}`, { method: 'PUT' });

export const completeLesson = (lessonId: string, sourcesOpened: readonly string[] = []) =>
  authed<CourseState>(`/api/me/lessons/${encodeURIComponent(lessonId)}/complete`, {
    method: 'PUT', body: JSON.stringify({ sourcesOpened }),
  });

export const recordCheckpoint = (itemId: string, lessonId: string, correct: boolean | null) =>
  authed<void>(`/api/me/checkpoints/${encodeURIComponent(itemId)}`, {
    method: 'PUT', body: JSON.stringify({ lessonId, correct }),
  });

export const putBookmark = (id: string, ref: { type: string; id: string }, note?: string) =>
  authed<void>(`/api/me/bookmarks/${encodeURIComponent(id)}`, {
    method: 'PUT', body: JSON.stringify(note === undefined ? { ref } : { ref, note }),
  });

export const removeBookmark = (id: string) =>
  authed<void>(`/api/me/bookmarks/${encodeURIComponent(id)}`, { method: 'DELETE' });

export const saveProfile = (patch: Partial<Profile>) =>
  authed<void>('/api/me/profile', { method: 'PUT', body: JSON.stringify(patch) });

export const noteActivity = (ref: { type: string; id: string }, action: string) =>
  authed<void>('/api/me/activity', { method: 'POST', body: JSON.stringify({ ref, action }) });

export const exportMyData = () => authed<Record<string, unknown>>('/api/me/export');

// ── comentarios nos cursos ─────────────────────────────────────

export interface CourseComment {
  readonly id: string;
  readonly displayName: string;
  readonly text: string;
  readonly createdAt: string;
}

/**
 * A leitura nao passa por `authed`: e publica, e exigir sessao aqui daria 401
 * a um visitante e a pagina pareceria partida quando estava a funcionar.
 */
export const fetchCourseComments = async (courseId: string): Promise<{ items: CourseComment[] }> => {
  const res = await fetch(`${base}/api/courses/${encodeURIComponent(courseId)}/comments`, {
    headers: { Accept: 'application/json', ...(await appCheckHeader()) },
  });
  if (!res.ok) throw new Error(`${res.status} comments`);
  return res.json() as Promise<{ items: CourseComment[] }>;
};

export type PostCommentOutcome =
  | { readonly ok: true; readonly id: string }
  /** Sessao anonima, ou conta ligada sem nome no perfil. */
  | { readonly ok: false; readonly reason: 'link_account_required' | 'display_name_required' };

export const postCourseComment = async (
  courseId: string, text: string,
): Promise<PostCommentOutcome> => {
  const token = await authClient().token();
  if (token === null) return { ok: false, reason: 'link_account_required' };
  const res = await fetch(`${base}/api/me/courses/${encodeURIComponent(courseId)}/comments`, {
    method: 'POST',
    headers: {
      Accept: 'application/json', 'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`, ...(await appCheckHeader()),
    },
    body: JSON.stringify({ text }),
  });
  if (res.status === 201) return { ok: true, ...(await res.json() as { id: string }) };
  // Os dois casos que a UI sabe resolver tem tratamento proprio; o resto sobe.
  if (res.status === 403) return { ok: false, reason: 'link_account_required' };
  if (res.status === 409) return { ok: false, reason: 'display_name_required' };
  throw new Error(`${res.status} comment`);
};

export const deleteCourseComment = (id: string) =>
  authed<void>(`/api/me/comments/${encodeURIComponent(id)}`, { method: 'DELETE' });

export const reportCourseComment = (id: string, reason: string) =>
  authed<void>(`/api/me/comments/${encodeURIComponent(id)}/report`, {
    method: 'POST', body: JSON.stringify({ reason }),
  });

// ── moderacao ──────────────────────────────────────────────────

export interface ModeratedComment extends CourseComment {
  readonly courseId: string;
  readonly status: 'visible' | 'hidden';
  readonly moderatedBy?: string;
  readonly moderatedAt?: string;
  readonly moderationReason?: string;
}
export interface ModerationQueue {
  readonly pending: readonly {
    readonly report: { id: string; reason: string; createdAt: string };
    readonly comment: ModeratedComment;
  }[];
  readonly hidden: readonly ModeratedComment[];
}

/** Sem a claim de moderacao isto responde 404 — a area nao anuncia que existe. */
export const fetchModerationQueue = () => authed<ModerationQueue>('/api/moderation/queue');

export const moderateComment = (id: string, action: 'hide' | 'restore', reason: string) =>
  authed<void>(`/api/moderation/comments/${encodeURIComponent(id)}/${action}`, {
    method: 'POST', body: JSON.stringify({ reason }),
  });

export const deleteMyAccount = () => authed<void>('/api/me', { method: 'DELETE' });

export const mergeFrom = (fromToken: string) =>
  authed<{ merged: Record<string, number> }>('/api/me/merge', {
    method: 'POST', body: JSON.stringify({ fromToken }),
  });

/** §55 — o que mudou desde a versão em que a pessoa concluiu. */
export const fetchCourseChanges = (
  rev: ContentRev, locale: Locale, courseId: string, since: number,
): Promise<{ from: number; to: number; changed: string[]; lessons?: { id: string; title: string }[] }> =>
  fetch(`${base}/api/v${rev.version}/${locale}/courses/${encodeURIComponent(courseId)}/changes?since=${since}`)
    .then((r) => r.json() as Promise<{ from: number; to: number; changed: string[] }>);

// ── camada de IA ([06]) ────────────────────────────────────────
export interface AiSegment {
  readonly text: string;
  readonly epistemic_label: string;
  readonly citations: readonly string[];
  readonly entity_refs: readonly { type: string; id: string }[];
  readonly source_layer?: string;
}

export interface AiAnswer {
  readonly segments: readonly AiSegment[];
  readonly coverage: 'full' | 'partial' | 'not_covered';
  readonly gaps: readonly string[];
  readonly packetHash: string;
  readonly cached: boolean;
  readonly suggested_actions: readonly {
    kind: string; label: string; ref?: { type: string; id: string };
  }[];
}

/**
 * Uma resposta que nao passou a validacao chega aqui como 422, e nao como
 * erro de rede: a UI mostra que o sistema recusou entregar, em vez de um
 * "tenta outra vez" que sugere que o problema foi a ligacao.
 */
export class AnswerRejected extends Error {
  constructor(readonly problems: readonly { kind: string; detail: string }[]) {
    super('failed_validation');
    this.name = 'AnswerRejected';
  }
}

export async function askAi(input: {
  message: string; locale: Locale; anchor: { type: string; id: string } | null;
}): Promise<AiAnswer> {
  const token = await authClient().token();
  const res = await fetch(`${base}/api/ai/chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token ?? ''}`,
      // O gateway de IA exige App Check ([10 §2]): e a rota que custa dinheiro.
      ...(await appCheckHeader()),
    },
    body: JSON.stringify(input),
  });
  if (res.status === 422) {
    const body = await res.json() as { problems: { kind: string; detail: string }[] };
    throw new AnswerRejected(body.problems);
  }
  if (!res.ok) throw new Error(`${res.status} /api/ai/chat`);
  return res.json() as Promise<AiAnswer>;
}

export const meKeys = {
  comments: (courseId: string) => ['comments', courseId] as const,
  moderationQueue: ['me', 'moderation'] as const,
  dashboard: ['me', 'dashboard'] as const,
  enrollments: ['me', 'enrollments'] as const,
  lessonProgress: ['me', 'lesson-progress'] as const,
  profile: ['me', 'profile'] as const,
  bookmarks: ['me', 'bookmarks'] as const,
  checkpoints: ['me', 'checkpoints'] as const,
};

/**
 * §47 — o limiar a partir do qual vale a pena avisar que o progresso não está
 * protegido.
 *
 * Avisar ao primeiro clique é ruído que se aprende a ignorar, e depois o aviso
 * que importa também passa despercebido. Isto é uma lição concluída ou três
 * itens guardados: trabalho suficiente para doer perdê-lo.
 */
export const hasProgressWorthProtecting = (
  lessons: number, bookmarks: number,
): boolean => lessons >= 1 || bookmarks >= 3;
